// Stage / display-mode resolvers extracted verbatim from InteractiveGraph.js
// (IG-split Slice 4, pure-function seam). All pure: they read stage/graph data
// objects and return keys, flags, or display-mode strings with no closure over
// component state. TRIG_STAGE_MODE_ORDER stays module-internal (only
// resolveTrigDisplayMode consumes it), matching its original inline scope.

export const buildStageAnimationKey = (graphData, stageData, currentStageNum, extraMode = '') => {
  const graphKey =
    graphData?.graph_id ||
    graphData?.id ||
    graphData?.graph_key ||
    graphData?.title ||
    graphData?.graph_type ||
    'graph';
  const visibleElements = Array.isArray(stageData?.visible_elements)
    ? [...stageData.visible_elements].sort().join('|')
    : '';
  return [
    graphKey,
    stageData?.graph_type || graphData?.graph_type || '',
    stageData?.step || '',
    currentStageNum ?? '',
    visibleElements,
    stageData?.show_all_with_original_style ? 'show-all' : 'stage',
    extraMode || ''
  ].join('::');
};

export const getDifficultyTypeForStage = (stageData, graphData, studentProfile) => {
  return stageData?.difficulty_type ||
    graphData?.difficulty_type ||
    (Array.isArray(studentProfile?.difficulty_types) ? studentProfile.difficulty_types[0] : null) ||
    null;
};

// ─── ステージ順序制御 (axes → labels → legend → data → complete) ─────────
export const getStageVisibilityFlags = (stageData) => {
  const step = stageData?.step || '';
  const visibleElements = stageData?.visible_elements || [];
  const hasAxisInVisible = visibleElements.some(id => id && id.includes('axis'));
  const STEPS_WITH_LABELS = ['labels', 'axis_labels', 'tick_labels', 'legend', 'data', 'trend', 'complete'];
  const STEPS_WITH_LEGEND = ['legend', 'data', 'trend', 'complete'];
  const STEPS_WITH_DATA = ['data', 'trend', 'complete'];
  return {
    showAxisLines: hasAxisInVisible || step === 'axes' || STEPS_WITH_LABELS.includes(step) || stageData?.show_all_with_original_style === true,
    showAxisLabels: stageData?.show_axis_labels === true || STEPS_WITH_LABELS.includes(step) || stageData?.show_all_with_original_style === true,
    showTickLabels: stageData?.show_axis_labels === true || STEPS_WITH_LABELS.includes(step) || stageData?.show_all_with_original_style === true,
    showLegend: stageData?.show_legend === true || STEPS_WITH_LEGEND.includes(step) || stageData?.show_all_with_original_style === true,
    showData: STEPS_WITH_DATA.includes(step) || stageData?.show_all_with_original_style === true || visibleElements.some(id => id && !id.includes('axis') && !id.includes('legend') && !id.includes('label')),
    currentStep: step
  };
};

// ─── Trig / Function graph helpers (pure functions) ──────────────────────────

const TRIG_STAGE_MODE_ORDER = [
  'axes_only',
  'curve_only',
  'show_keypoint_dots',
  'show_keypoint_coords',
  'show_all'
];

export const resolveTrigDisplayMode = ({ stageData, currentStageNum = 0, isTrig = false } = {}) => {
  if (!isTrig) return null;
  const explicitMode = typeof stageData?.trig_display_mode === 'string'
    ? stageData.trig_display_mode.trim()
    : '';
  if (explicitMode) return explicitMode;
  if (stageData?.show_all_with_original_style === true) return 'show_all';

  const indexCandidates = [
    stageData?.stage_index,
    stageData?.stage,
    currentStageNum
  ];
  let stageIndex = 0;
  for (const candidate of indexCandidates) {
    const numeric = Number(candidate);
    if (Number.isFinite(numeric)) {
      stageIndex = Math.trunc(numeric);
      break;
    }
  }

  const clampedIndex = Math.max(0, Math.min(4, stageIndex));
  return TRIG_STAGE_MODE_ORDER[clampedIndex] || 'show_all';
};

// Resolve func_display_mode for non-trig function graphs (mirrors resolveTrigDisplayMode)
export const resolveFuncDisplayMode = ({ stageData, isTrig = false, isShowAllStage = false } = {}) => {
  if (isTrig) return null;
  const explicit = typeof stageData?.func_display_mode === 'string'
    ? stageData.func_display_mode.trim() : '';
  if (explicit) return explicit;
  // Legacy fallback: old data has show_all_with_original_style but no func_display_mode
  if (isShowAllStage) return 'show_all';
  return null;
};

// ─── End trig / function graph helpers ───────────────────────────────────────

// A-3: pick the stage-data object to show on (re)mount / graphData change. The stage
// INDEX is restored from graphData.current_stage (persisted server-side, the single
// source of truth); the stage DATA must match that index. Previously this always fell
// back to stages[0], so a reload that restored current_stage=3 rendered stage-0 data
// under a "stage 4" label. Prefer an explicit current_stage_data, else the stage AT the
// restored index, else the legacy first-stage fallbacks.
export const resolveInitialStageData = (graphData) => {
  if (!graphData) return null;
  if (graphData.current_stage_data) return graphData.current_stage_data;
  const restoredIndex = graphData.current_stage ?? 0;
  if (Array.isArray(graphData.stages) && graphData.stages[restoredIndex]) {
    return graphData.stages[restoredIndex];
  }
  return (
    graphData.first_stage ||
    (Array.isArray(graphData.stages) ? graphData.stages[0] : null) ||
    null
  );
};
